import { useMemo } from 'react';
import { useActiveAccountId } from '@frameio/shared/contexts/ActiveEntitiesContext';
import { appComponentsStorage } from '@frameio/shared/storage/appComponentsStorage';
import { datadogLogger as datadogLoggerHelper } from '@frameio/telemetry/datadog/logger';
import { auth } from '@frameio/utils/auth';
import { isUXPEnvironment } from '@frameio/utils/config';
import { useGetUxpHostInfoQuery } from '../gql/queries/generated/GetUxpHostInfo';
/**
 * Custom Datadog logger hook for Premiere Pro UXP integrations.
 *
 * Automatically enriches all log entries with:
 * - UXP host information (Premiere version, OS, etc.)
 * - Source identifier (`PrUXP`)
 *
 * @returns A logger object with three methods:
 * - `info(message, payload?)` - Log informational messages
 * - `log(message, payload?)` - Log general messages
 * - `error(message, payload?)` - Log error messages
 *
 * @example
 * ```tsx
 * const logger = useDatadogLogger();
 *
 * // Log with just a message
 * logger.info('Export started');
 *
 * // Log with additional payload
 * logger.error('Export failed', { exportId: '123', reason: 'timeout' });
 * ```
 */
export const useDatadogLogger = () => {
  const hostInfo = useGetUxpHostInfoQuery({
    context: { resolveWithUXP: true },
    skip: !isUXPEnvironment(),
  })?.data?.uxpHostInfo;

  const userId = useMemo(() => {
    return auth.getAccessTokenUserId(auth.getAccessToken() || '');
  }, []);
  const lastKnownAccountId = appComponentsStorage.get('lastViewedAccountId');
  const activeAccountId = useActiveAccountId();
  const accountId = activeAccountId || lastKnownAccountId;

  const uxpHostInfo = useMemo(() => {
    if (!hostInfo) return null;

    const info = { ...hostInfo };
    if (info && info.__typename) {
      delete info.__typename;
    }
    const clientName = (global || window).location.hostname;
    const releaseVersion = process.env.RELEASE_VERSION || 'unknown';
    const clientVersion = releaseVersion.split('@').pop() || 'unknown';

    return {
      client: `${clientName}/${clientVersion}`,
      clientName,
      clientVersion,
      uxpHostInfo: {
        ...info,
      },
      uxpUploads: {
        source: 'PrUXP',
      },
      userId,
      accountId,
    };
  }, [hostInfo, userId, accountId]);

  const datadogLogger = useMemo(() => {
    return {
      info: (message: string, payload?: any) => {
        datadogLoggerHelper.info(message, { ...uxpHostInfo, ...payload });
      },
      log: (message: string, payload?: any) => {
        datadogLoggerHelper.log(message, { ...uxpHostInfo, ...payload });
      },
      error: (message: string, payload?: any) => {
        datadogLoggerHelper.error(message, { ...uxpHostInfo, ...payload });
      },
    };
  }, [uxpHostInfo]);

  return datadogLogger;
};
